/* aestheticwolf — quick add drawer helpers (PDP v2 styling, see quick-add-v2.css)
   - Add to cart button shows the price, or "Select a size" until a size is picked
   - Opened from a product recommendation / set row: no size is pre-selected,
     except the size already picked on the product page (set rows)
   - Opened from a PDP v2 set row: shows the price in the set
   - "View full details" link to the product page
   - Mobile: a more compact gallery (about two images in view)
   Works on top of the theme's quick-cart-drawer.js and product-card logic. */
(() => {
  if (window.awQuickAddV2) return;
  window.awQuickAddV2 = true;

  const SIZE_NAMES = ['size', 'sizes', 'maat', 'taille', 'größe', 'grösse', 'talla'];
  const TEXT = {
    selectSize: 'Select a size',
    selectOption: 'Select an option',
    soldOut: 'Sold out',
    placeholder: 'Select',
    details: 'View full details',
    inSet: 'in the set',
    setNote: 'discount applied automatically'
  };

  let lastTrigger = null;
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('.quick-cart-drawer__trigger');
      if (trigger) lastTrigger = trigger;
    },
    true
  );

  const formatMoney = (cents) => {
    const format = window.theme && window.theme.settings && window.theme.settings.money_format;
    if (typeof window.formatPrice === 'function' && format) return window.formatPrice(cents, format);
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
  };

  const optionName = (fieldset) => {
    const legend = fieldset.querySelector('legend');
    if (!legend) return '';
    const first = legend.childNodes[0];
    return (first && first.nodeType === 3 ? first.textContent : legend.textContent).trim().toLowerCase();
  };

  const pdpSelectedSize = (trigger) => {
    const root = trigger && trigger.closest('.pdp-v2');
    if (!root) return null;
    const fieldsets = root.querySelectorAll('.pdp-v2-variants__fieldset');
    for (const fieldset of fieldsets) {
      const label = fieldset.querySelector('.pdp-v2-legend__label');
      if (label && SIZE_NAMES.includes(label.textContent.trim().toLowerCase())) {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      }
    }
    return null;
  };

  // Mobile: show about two images side by side instead of 1.25 so the gallery
  // takes less height and the size picker and add to cart fit in the sheet.
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const MOBILE_SLIDES = 2.15;
  const compactGallery = (product, attempt = 0) => {
    const el = product.querySelector('.quick-cart-drawer__media-swiper');
    if (!el) return;
    const swiper = el.swiper;
    if (!swiper) {
      if (attempt < 20) window.requestAnimationFrame(() => compactGallery(product, attempt + 1));
      return;
    }
    if (swiper.originalParams) swiper.originalParams.slidesPerView = MOBILE_SLIDES;
    if (MOBILE.matches) {
      swiper.params.slidesPerView = MOBILE_SLIDES;
      swiper.update();
    }
  };

  const enhance = (drawer) => {
    const product = drawer.querySelector('.quick-cart-product');
    if (!product || product.dataset.awEnhanced === 'true') return;
    product.dataset.awEnhanced = 'true';
    compactGallery(product);

    const trigger = lastTrigger;
    const form = product.querySelector('.product-card__add-to-cart--form');
    const button = form && form.querySelector('button[type="submit"]');
    if (!button) return;

    let variants = [];
    try {
      variants = JSON.parse(product.querySelector('[data-product-variants-json]').textContent);
    } catch (error) {
      variants = [];
    }

    const fieldsets = [...product.querySelectorAll('.js-product-card-options')].filter(
      (fieldset) => !fieldset.parentElement.classList.contains('hidden')
    );
    const sizeFieldset = fieldsets.find((fieldset) => SIZE_NAMES.includes(optionName(fieldset)));
    const baseLabel = button.textContent.trim();

    button.innerHTML =
      '<span class="aw-qa-atc__label"></span>' +
      '<i class="aw-qa-atc__price" aria-hidden="true"><i class="aw-qa-atc__dot">&bull;</i><i data-aw-qa-price></i></i>';
    const labelEl = button.querySelector('.aw-qa-atc__label');
    const priceEl = button.querySelector('[data-aw-qa-price]');

    // Recommendations and set rows: let the shopper pick the size (or carry over the PDP size)
    const fromRecommendation = trigger && trigger.classList.contains('quick-cart-drawer__trigger--recommendations');
    if (fromRecommendation && sizeFieldset) {
      const inputs = [...sizeFieldset.querySelectorAll('input[type="radio"]')];
      if (inputs.length > 1) {
        inputs.forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });
        const wanted = pdpSelectedSize(trigger);
        const match = wanted && inputs.find((input) => input.value === wanted && !input.disabled);
        if (match) {
          match.click();
        } else if (!button.hasAttribute('disabled')) {
          button.setAttribute('disabled', '');
        }
      }
    }

    // Price in the set, when opened from a PDP v2 set row
    const setRow = trigger && trigger.closest('[data-pdp-v2-set-row]');
    const setPrice = setRow && setRow.querySelector('[data-set-row-set-price]');
    const priceWrap = product.querySelector('.product__price');
    if (setPrice && priceWrap && !product.querySelector('.aw-qa-set-note')) {
      const note = document.createElement('p');
      note.className = 'aw-qa-set-note';
      note.innerHTML = `<strong>${setPrice.textContent.trim()}</strong> ${TEXT.inSet} · ${TEXT.setNote}`;
      priceWrap.insertAdjacentElement('afterend', note);
    }

    // Link to the full product page
    const titleLink = product.querySelector('a.product__title');
    const container = product.querySelector('.product__content .container');
    if (titleLink && container && !product.querySelector('.aw-qa-details')) {
      const link = document.createElement('a');
      link.className = 'aw-qa-details';
      link.href = titleLink.getAttribute('href');
      link.textContent = TEXT.details;
      container.appendChild(link);
    }

    const sync = () => {
      let allChosen = true;
      fieldsets.forEach((fieldset) => {
        const chosen = fieldset.querySelector('input[type="radio"]:checked');
        const value = fieldset.querySelector('legend [data-selected-variant]');
        if (!chosen) {
          allChosen = false;
          if (value) {
            value.textContent = TEXT.placeholder;
            value.classList.add('is-placeholder');
          }
        } else if (value) {
          if (value.classList.contains('is-placeholder')) value.textContent = chosen.value;
          value.classList.remove('is-placeholder');
        }
      });

      if (!allChosen) {
        labelEl.textContent = sizeFieldset && !sizeFieldset.querySelector('input[type="radio"]:checked') ? TEXT.selectSize : TEXT.selectOption;
        priceEl.textContent = '';
        if (!button.hasAttribute('disabled')) button.setAttribute('disabled', '');
        return;
      }

      let variant;
      if (fieldsets.length) {
        const selected = fieldsets.map((fieldset) => fieldset.querySelector('input[type="radio"]:checked').value);
        variant = variants.find((item) => item.options.join() === selected.join());
      } else {
        const idInput = form.querySelector('input[name="id"]');
        variant = variants.find((item) => String(item.id) === (idInput && idInput.value)) || variants[0];
      }
      if (!variant || !variant.available) {
        labelEl.textContent = TEXT.soldOut;
        priceEl.textContent = '';
        return;
      }
      labelEl.textContent = baseLabel;
      priceEl.textContent = formatMoney(variant.price);
    };

    new MutationObserver(sync).observe(button, { attributes: true, attributeFilter: ['disabled'] });
    product.addEventListener('click', (event) => {
      if (event.target.matches('input[type="radio"]')) window.setTimeout(sync, 0);
    });
    product.addEventListener('change', () => window.setTimeout(sync, 0));
    sync();
  };

  const watch = () => {
    const drawer = document.querySelector('quick-cart-drawer');
    const main = drawer && drawer.querySelector('.quick-cart-drawer__main');
    if (!main || main.dataset.awWatched === 'true') return;
    main.dataset.awWatched = 'true';
    new MutationObserver(() => enhance(drawer)).observe(main, { childList: true });
    enhance(drawer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
  document.addEventListener('shopify:section:load', watch);
})();
